import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { cloneDeep, isEmpty, isUndefined, isNull, debounce } from 'lodash';
import useApi from '../../../hooks/useApi';
import useAccountSettings from '../../../hooks/useAccountSettings';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Row, Col, Dropdown, Menu, Input, Select, Modal, Form } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined} from '@ant-design/icons';
import { MdOutlineMoreHoriz, MdOutlineClose, MdChecklist, MdArrowForwardIos } from "react-icons/md";
import { deleteEvent, getAccountEventStages, updateEventStage, getEventsSearch } from '../../../api';
import LoadingSpinner from '../../../components/loading';
import AdminContent from '../../../components/adminContent';
import EventCard from '../../../components/eventCard';
import emptySearchImage from '../../../images/empty-search-icon.png';
import FadeWhileInView from '../../../components/fadeWhileInView'
import FloatingContainer from '../../../components/floatingContainer'
import { LoadingOutlined} from '@ant-design/icons';
import {
  renderSelectField
} from '../../../components/formFields'

const { Option } = Select; 

const EventSearchPage = () => {

  const [searchText, setSearchText] = useState("");
  const [isConfirmRemoveVisible, setConfirmRemoveVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({});
  const [isDeleting, setDeleting] = useState(false);
  const [isDeleteError, setDeleteError] = useState(false);
  const [eventStagesData, setEventStagesData] = useState([]);
  const [isUpdateStageModalVisible, setUpdateStageModalVisible] = useState(false);
  const [isSearching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchLimit, setSearchLimit] = useState(30);
  const [currentSearchOffset, setCurrentSearchOffset] = useState(0);

  useDocumentTitle("Event Search")
  const navigate = useNavigate();
  const location = useLocation()
  const [sendRequest] = useApi()
  const [accountSettings] = useAccountSettings()

  const [editStageForm] = Form.useForm();

  useEffect(() => {
    window.scrollTo(0, 0);
    const queryStrings = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (queryStrings.q) {
      setSearching(true)
      setSearchText(queryStrings.q)
      debouncedSearch(queryStrings.q)
    }
  }, []);

  const isValid = (value) => {
    return !isNull(value) && !isUndefined(value) && !isEmpty(value)
  }

  const removeEvent = (event) => {
    setConfirmRemoveVisible(true)
    setDeleteError(false)
    setSelectedEvent(event)
  }

  const confirmDeleteEvent = async () => {
    setDeleteError(false)
    try {
      if (selectedEvent.event_id) {
        setDeleting(true)
        const deleteResults = await sendRequest(deleteEvent(selectedEvent.event_id));
        if (deleteResults.success) {
          debouncedSearch(searchText)
          setConfirmRemoveVisible(false)
        } else {
          setDeleteError(true)
        }
        setDeleting(false)
      } else {
        setDeleteError(true)
      }
    } catch (error) {
      setDeleteError(true)
      setDeleting(false)
    }
  }

  const onSearchTextChange = (text) => {
    setSearching(true)
    setSearchText(text)
    updateUrl(text)
    if (!isEmpty(text)) {
      debouncedSearch(text)
    } else {
      setSearchResults([])
      setSearching(false)
    }
  }

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        try {
          if (!isEmpty(query)) {
            const body = {
              search_term: query,
              offset: 0,
              limit: searchLimit
            }
            const results = await sendRequest(getEventsSearch(body))
            setSearchResults(results.results)
            setTotalSearchResults(results.metadata.total)
            setCurrentSearchOffset(0)
          } else {
            setSearchResults([])
          }
          setSearching(false)
        } catch {

        }
      }, 500),
    []
  );

  const loadMoreSearchResults = async () => {
    try {
      const newOffset = Number(currentSearchOffset) + searchLimit
      const body = {
        search_term: searchText,
        offset: newOffset,
        limit: searchLimit
      }
      const results = await sendRequest(getEventsSearch(body))
      var newEvents = cloneDeep(searchResults)
      newEvents.concat(results.results)
      setSearchResults(newEvents.concat(results.results))
      setTotalSearchResults(results.metadata.total)
      setCurrentSearchOffset(results.metadata.offset)
    } catch {

    }
  }

  const updateUrl = (query) => {
    var url = "/admin/events/search"
    var params = {}

    if (isValid(query)) {
      params.q = query
    }

    // Stringify params
    const paramString = qs.stringify(params)

    if (!isEmpty(params)) {
      url += `?${paramString}`
    }

    navigate(url, { replace: true })
  }

  const onUpdateStage = async (event) => {
    try {
      setSelectedEvent(event)
      editStageForm.setFieldsValue({ stage_id: event.stage_id })
      setUpdateStageModalVisible(true)

      if (eventStagesData.length == 0) {
        const eventStageResults = await sendRequest(getAccountEventStages())
        const eventStageDataResults = eventStageResults.map(x => {
          return {
            text: x.stage_name,
            value: x.stage_id
          }
        })
        setEventStagesData(eventStageDataResults)
      }
    } catch { }
  }

  const cancelUpdateStage = () => {
    setUpdateStageModalVisible(false)
  }

  const onUpdateStageSubmit = async (values) => {
    try {
      // Ignore if the event stage hasn't changed
      if (selectedEvent.stage_id == values["stage_id"]) {
        setUpdateStageModalVisible(false)
        return
      }

      await sendRequest(updateEventStage(selectedEvent.event_id, { stage_id: values["stage_id"] }));
      // Refresh search results
      debouncedSearch(searchText)
      setUpdateStageModalVisible(false)
    } catch {
      setUpdateStageModalVisible(false)
    }
  }

  const menu = (event) => {
    return (
      <Menu>
        <Menu.Item>
          <div onClick={() => onUpdateStage(event)}>
            <MdChecklist style={{ marginRight: 8}}/> Update Stage
          </div>
        </Menu.Item>
        <Menu.Item>
          <div onClick={() => navigate(`/admin/events/${event.event_id}`)}>
            <EditOutlined style={{ marginRight: 8}}/> Edit
          </div>
        </Menu.Item>
        <Menu.Item>
          <div onClick={() => removeEvent(event)}>
            <DeleteOutlined style={{ marginRight: 8}}/> Delete
          </div>
        </Menu.Item>
      </Menu>
    )
  };

  const renderRightIcon = (event) => {
    return (
      <div className="display-flex mr-5" onClick={(e) => e.stopPropagation()}>
        <Dropdown overlay={menu(event)} placement="bottomRight" trigger="click">
          <div className="dots-container">
            <MdOutlineMoreHoriz style={{ fontSize: 24, color: '#999'}}/>
          </div>
        </Dropdown>
      </div>
    )
  }

  const renderEventCard = (event, index, totalGroupEvents) => {
    var showBorder = index < totalGroupEvents - 1
    return (
      <EventCard 
        event={event} 
        accountSettings={accountSettings} 
        rightIcon={renderRightIcon(event)} 
        className={showBorder ? "b-border" : ""} 
        onClick={() => navigate(`/admin/events/${event.event_id}`)} 
        showType={true} 
        showStatus={true}
        key={index}/>
    );
  }

  const renderConfirmRemoveModal = () => {
    return (
      <Modal visible={isConfirmRemoveVisible} closable={false} footer={null} width={400} wrapClassName="rounded-modal">
        <div className="text-right" onClick={() => setConfirmRemoveVisible(false)}><MdOutlineClose size={30} color={"#CCC"}/></div>
        <div className="mt-10">
          <div className="fw-700 fs-20 text-center">Delete Event</div>
          <div className="fw-500 fs-14 mt-20 mb-20 text-center">Are you sure you would like to delete the event <span className="fw-700">{selectedEvent.event_name}</span>? All data will be removed and any clients that are assigned to this event will no longer have access.</div>
          { isDeleting ? (
            <button className="primary-button warning" type="button"><LoadingOutlined/></button>
          ) : (
            <button className="primary-button warning" type="button" onClick={() => confirmDeleteEvent()}>Delete Event</button>
          )}
          { isDeleteError && (
            <div className="text-center mt-5 c-red">Something went wrong. Please try again.</div>
          )}
          <div className="text-center mt-15">
            <div className="blue-link" onClick={() => setConfirmRemoveVisible(false)}>Cancel</div>
          </div>
        </div>
      </Modal>
    )
  }

  const renderUpdateStageModal = () => {
    return (
      <Modal visible={isUpdateStageModalVisible} closable={false} footer={null} width={600} wrapClassName="rounded-modal">
        <Row align="middle" className="b-border pb-10">
          <Col flex={1}>
            <div className="fw-700 fs-18">Update Stage</div>
          </Col>
          <Col>
            <div className="display-flex" onClick={() => cancelUpdateStage()}><MdOutlineClose size={30} color={"#CCC"}/></div>
          </Col>
        </Row>
        <div className="mt-10">
          <Form form={editStageForm} layout="vertical" name="edit-client-role" onFinish={onUpdateStageSubmit}>
            <Row gutter={[20,10]}>
              <Col xs={24} md={24}>
                { renderSelectField("Event Stage", "stage_id", "Select a stage...", eventStagesData, true) }
              </Col>
            </Row>
            <div className="admin-modal-footer">
              <button className="page-title-button" type="submit">Save</button>
            </div>
          </Form>
        </div>
      </Modal>
    )
  }

  const renderHeader = () => {
    return (
      <div className="p-20">
        <div>
          <span className="c-blue fw-700 cursor-default" onClick={() => navigate("/admin/events")}>
            Events
          </span>
          <span className="fs-10 mh-5"><MdArrowForwardIos/></span>
          <span className="cursor-default c-text-gray">
            Search
          </span>
        </div>
        <Row align="middle" gutter={[15,15]}>
          <Col xs={24}>
            <div className="fw-700 fs-24 mt-5">Event Search</div>
          </Col>
          <Col xs={24}>
            <Input className="list-search-input-square" size="large" placeholder="Event name, date, or client" value={searchText} onChange={(e) => onSearchTextChange(e.target.value)} allowClear={true} prefix={<SearchOutlined color="#e4e4e4" style={{ marginRight: 6 }}/>} />
          </Col>
        </Row>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <div>
       <FloatingContainer className="ph-20" verticalPadding={20} maxWidth={1000}>
          { renderSearchEvents() }
        </FloatingContainer>
      </div>
    )
  }

  const renderSearchEvents = () => {
    if (isSearching) {
      return (
        <div className="mt-30 text-center">
          <LoadingSpinner/>
        </div>
      );
    }
    if (isEmpty(searchText)) {
      return (
        <FadeWhileInView duration={0.2} className="ph-20 pv-40 text-center shadow-card">
          <div>
            <img src={emptySearchImage} width={200}/>
          </div>
          <div className="fs-18 fw-700 mt-30">
            Search events
          </div>
          <div className="fs-14 fw-500 c-text-gray">
            Enter a search term to find an event
          </div>
        </FadeWhileInView>
      )
    }
    if (searchResults.length == 0) {
      return (
        <FadeWhileInView duration={0.2} className="ph-20 pv-40 text-center shadow-card">
          <div>
            <img src={emptySearchImage} width={200}/>
          </div>
          <div className="fs-18 fw-700 mt-30">
            No events found
          </div>
          <div className="fs-14 fw-500 c-text-gray">
            Your search did not return any results. Please try again.
          </div>
        </FadeWhileInView>
      )
    }
    return (
      <div>
        <div className="ml-5 mb-5 fw-700">Results for "{searchText}" <span className="c-text-gray fw-500">({totalSearchResults})</span></div>
        <div className="shadow-card mb-10">
          { searchResults.map((item, i) => renderEventCard(item, i, searchResults.length)) }
        </div>
        { totalSearchResults > searchResults.length && (
          <div className="text-center mt-20">
            <div className="blue-link" onClick={() => loadMoreSearchResults()}>Load more</div>
          </div>
        )}
        { renderConfirmRemoveModal() }
        { renderUpdateStageModal() }
      </div>
    )
  }

  return (
    <AdminContent body={renderContent()} header={renderHeader()}/>
  );
}

export default EventSearchPage;
